import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import clsx from 'clsx'

import { FormHelperText, Grid, OutlinedInput } from '@mui/material'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles({
  inputContainer: {
    height: 60,
    '& .MuiOutlinedInput-root': {
      width: ({ width }: IInputCodeField) => width,
      height: ({ height }: IInputCodeField) => height,
      '& input': {
        textAlign: 'center',
        padding: '0',
      },
    },
  },
  formControl: {
    height: 40,
    boxSizing: 'border-box',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  errorTxt: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E02020',
    },
  },
})

type Inputs = {
  twoFaCode: string
}

type IInputCodeField = {
  width: number | string
  height: number | string
  isError?: boolean
  getPressEnterEvent?: (isEnterPressed: boolean, data: Inputs) => void
}

export const InputCodeField: FC<IInputCodeField> = ({ width, height, isError, getPressEnterEvent }) => {
  const { inputContainer, formControl, errorTxt } = useStyles({ width, height })
  const initialValue = { d1: '', d2: '', d3: '', d4: '', d5: '', d6: '' }

  const [code, setCode] = useState(initialValue)
  const [codeStr, setCodeStr] = useState('')
  const [error, setError] = useState('')
  const startInput = useRef<HTMLInputElement>(null)

  const {
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext()

  const handleChange = useCallback(
    (event) => {
      const { id, value } = event.target
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(value)) {
        if (['d1', 'd2', 'd3', 'd4', 'd5', 'd6'].includes(id)) {
          const nextInputId = +id[1] + 1
          const nextInput = document.getElementById(`d${nextInputId}`)
          setCode((prevState) => {
            return {
              ...prevState,
              [id]: value,
            }
          })
          if (value !== '') {
            // eslint-disable-next-line no-unused-expressions
            nextInput && nextInput.focus()
          }
        }
      }
    },
    // eslint-disable-next-line
    [setCode],
  )

  useEffect(() => {
    setValue('twoFaCode', Object.values(code).join(''))
    setCodeStr(getValues(['twoFaCode'])[0])
  }, [code, getValues, setValue])

  const handleKeyPress = useCallback(
    (event) => {
      if (event && event.key === 'Backspace') {
        const { id, value } = event.target
        const nextInputId = value === '' ? +id[1] - 1 : +id[1]
        const nextInput = document.getElementById(`d${nextInputId}`)
        // eslint-disable-next-line no-unused-expressions
        nextInput && nextInput.focus()
      }
      if (event && event.key === 'Enter') {
        if (codeStr?.length < 6) {
          setError('Please enter the code')
        } else if (getPressEnterEvent) {
          setError('')
          getPressEnterEvent(true, { twoFaCode: codeStr })
        }
      }
    },
    [codeStr, getPressEnterEvent],
  )

  return (
    <Grid container className={inputContainer} direction={'column'} wrap={'nowrap'}>
      <Grid container item className={clsx(formControl, isError ? errorTxt : '')}>
        <OutlinedInput
          inputProps={{
            maxLength: 1,
          }}
          autoFocus
          id="d1"
          value={code.d1}
          type={'text'}
          color={'primary'}
          onChange={handleChange}
          inputRef={startInput}
          onKeyDown={handleKeyPress}
        />
        <OutlinedInput
          inputProps={{
            maxLength: 1,
          }}
          id="d2"
          value={code.d2}
          type={'text'}
          color={'primary'}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
        <OutlinedInput
          inputProps={{
            maxLength: 1,
          }}
          id="d3"
          value={code.d3}
          type={'text'}
          color={'primary'}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
        <OutlinedInput
          inputProps={{
            maxLength: 1,
          }}
          id="d4"
          value={code.d4}
          type={'text'}
          color={'primary'}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
        <OutlinedInput
          inputProps={{
            maxLength: 1,
          }}
          id="d5"
          value={code.d5}
          type={'text'}
          color={'primary'}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
        <OutlinedInput
          inputProps={{
            maxLength: 1,
          }}
          id="d6"
          value={code.d6}
          type={'text'}
          color={'primary'}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
      </Grid>
      <Grid item>{(error || errors) && <FormHelperText>{error || errors.twoFaCode?.message}</FormHelperText>}</Grid>
    </Grid>
  )
}
